import { notifyError } from "@/lib/notify-error";
import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useProject } from "@/components/projects";
import { FormActions, PageHeader } from "@/components/layout";
import {
  getEmptyPageSelectionError,
  getSelectedPageNumbers,
  ProjectDocumentPicker,
  type PickerDocument,
  type PickerSection,
  type SectionSelections,
} from "@/components/project-document-picker";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { EditAccessGate } from "@/features/onboarding";

export const Route = createFileRoute("/specPilot/new")({
  component: NewSubmittalPage,
});

const formSchema = z.object({
  name: z.string().min(1, "SpecPilot job name is required"),
});

type FormValues = z.infer<typeof formSchema>;

export function NewSubmittalPage() {
  return (
    <EditAccessGate>
      <NewSubmittalPageContent />
    </EditAccessGate>
  );
}

function NewSubmittalPageContent() {
  const navigate = useAppNavigate();
  const { projectId, isLoading: isProjectLoading } = useProject();

  const createJob = useMutation(api.specPilot.submittals.createFromRevision);

  const [pickerValues, setPickerValues] = useState<
    Record<string, SectionSelections>
  >({ schedules: { mode: "single", selection: null } });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserEditedName, setHasUserEditedName] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema as never),
    defaultValues: { name: "" },
  });

  const { data: scheduleOptionsRaw } = useQuery(
    convexQuery(
      api.projectDocumentRevisionOptions.listClassifiedPdfRevisionOptions,
      projectId ? { projectId, documentType: "schedule" } : "skip"
    )
  );

  const scheduleDocs: PickerDocument[] = (scheduleOptionsRaw ?? []).map(
    (opt) => ({
      _id: String(opt._id),
      name: opt.name,
      revisions: opt.revisions.map((r) => ({
        _id: String(r._id),
        revisionNumber: r.revisionNumber,
        revisionName: r.revisionName,
      })),
    })
  );

  const sections: PickerSection[] = [
    {
      key: "schedules",
      label: "Schedules",
      documents: scheduleDocs,
      selectionMode: "single",
      revisionMode: "single",
      addLabel: "Add Schedule",
      pageKind: "schedule",
    },
  ];

  useEffect(() => {
    if (!isProjectLoading && !projectId) {
      navigate({ to: "/projects", title: "Projects" });
    }
  }, [isProjectLoading, projectId, navigate]);

  useEffect(() => {
    setPickerValues({ schedules: { mode: "single", selection: null } });
    setHasUserEditedName(false);
  }, [projectId]);

  // Auto-populate name when a revision is selected
  const selectedSchedule =
    pickerValues.schedules?.mode === "single" ? pickerValues.schedules.selection : null;
  const selectedRevisionId = selectedSchedule?.revisionId;
  const selectedDocumentId = selectedSchedule?.documentId;

  useEffect(() => {
    if (!selectedRevisionId || !selectedDocumentId) return;
    const doc = scheduleOptionsRaw?.find(
      (o) => String(o._id) === selectedDocumentId
    );
    const rev = doc?.revisions.find(
      (r) => String(r._id) === selectedRevisionId
    );
    if (rev && !hasUserEditedName) {
      form.setValue("name", rev.originalFilename.replace(/\.pdf$/i, ""), {
        shouldValidate: true,
      });
    }
  }, [selectedRevisionId, selectedDocumentId, scheduleOptionsRaw, form, hasUserEditedName]);

  const handlePickerChange = useCallback(
    (sectionKey: string, value: SectionSelections) => {
      setPickerValues((prev) => ({ ...prev, [sectionKey]: value }));
    },
    []
  );

  const onSubmit = async (values: FormValues) => {
    if (!projectId) {
      toast.error("Please select a project first");
      return;
    }
    if (!selectedRevisionId) {
      toast.error("Select a schedule document and revision");
      return;
    }
    const emptyPagesError = getEmptyPageSelectionError([selectedSchedule]);
    if (emptyPagesError) {
      toast.error(emptyPagesError);
      return;
    }

    setIsSubmitting(true);
    try {
      const jobId = await createJob({
        projectId,
        revisionId: selectedRevisionId as Id<"projectDocumentRevisions">,
        pageNumbers: getSelectedPageNumbers(selectedSchedule),
        name: values.name,
      });
      toast.success("SpecPilot job created and processing started");
      void navigate({ to: "/specPilot/$jobId", params: { jobId } });
    } catch (error) {
      console.error("Submission error:", error);
      notifyError(error, "Failed to create SpecPilot job. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="New SpecPilot Job"
        trail={[{ label: "SpecPilot", href: "/specPilot" }]}
        description="Select a classified schedule from the project library. We will extract its tables for this job."
      />

      <Card className="max-w-3xl">
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SpecPilot Job Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter SpecPilot job name"
                        name={field.name}
                        ref={field.ref}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(event) => {
                          setHasUserEditedName(true);
                          field.onChange(event);
                        }}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormDescription>
                      Auto-populated when you select a revision.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ProjectDocumentPicker
                sections={sections}
                values={pickerValues}
                onChange={handlePickerChange}
                isLoading={scheduleOptionsRaw === undefined}
                disabled={isSubmitting}
              />

              <FormActions
                cancelTo="/specPilot"
                cancelTitle="SpecPilot"
                submitLabel={isSubmitting ? "Creating..." : "Create SpecPilot Job"}
                submitting={isSubmitting}
              />
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
